import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Search, ShieldAlert, Ban, CheckCircle2, Trash2, KeyRound, Eye, UserPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const RANK = { user: 1, admin: 2, superadmin: 3 };
const ROLE_COLOR = { user: "#78716c", admin: "#0E7490", superadmin: "#9A3412" };

export default function AdminUsers() {
  const { user, loading, startEmulate } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "user" });

  const load = useCallback(async (term = "") => {
    setBusy(true);
    try { const { data } = await api.get("/admin/users", { params: term ? { q: term } : {} }); setRows(data || []); }
    catch (e) { if (e.response?.status === 403) { toast.error("Admin access required"); navigate("/"); } }
    finally { setBusy(false); }
  }, [navigate]);

  useEffect(() => {
    if (loading) return;
    if (!user || RANK[user.role] < 2) { navigate("/"); return; }
    load();
  }, [user, loading, navigate, load]);

  const myRank = RANK[user?.role] || 1;
  const canAssign = (role) => RANK[role] < myRank || (myRank === 3 && RANK[role] === 3);
  const canManage = (r) => (r.email !== user?.email || user?.impersonated_by) && (RANK[r.role] < myRank || (myRank === 3 && RANK[r.role] === 3));

  const act = async (fn, okMsg) => {
    try { await fn(); toast.success(okMsg); await load(q); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const changeRole = (r, role) => act(() => api.put(`/admin/users/${r.id}/role`, { role }), "Role updated");
  const block = (r) => act(() => api.put(`/admin/users/${r.id}/block`), "User blocked");
  const unblock = (r) => act(() => api.put(`/admin/users/${r.id}/unblock`), "User unblocked");
  const del = (r) => { if (window.confirm(`Delete ${r.email}? This removes their favorites too.`)) act(() => api.delete(`/admin/users/${r.id}`), "User deleted"); };
  const resetPw = (r) => { const p = window.prompt(`New password for ${r.email} (min 6 chars):`); if (p) act(() => api.put(`/admin/users/${r.id}/password`, { password: p }), "Password reset"); };
  const emulate = async (r) => { try { await startEmulate(r.id); toast.success(`Now viewing as ${r.email}`); navigate("/my-map"); } catch (e) { toast.error(e.response?.data?.detail || "Could not emulate"); } };

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try { await api.post("/admin/users", form); toast.success("User created"); setForm({ email: "", password: "", name: "", role: "user" }); await load(q); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not create user"); }
    finally { setCreating(false); }
  };

  const assignableRoles = ["user", "admin", "superadmin"].filter(canAssign);

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-8 lg:px-12 py-8">
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent mb-1.5 flex items-center gap-2"><Users className="h-3.5 w-3.5" /> Administration</p>
      <h1 className="font-serif text-3xl sm:text-4xl font-bold text-foreground">User Management</h1>
      <p className="mt-2 text-muted-foreground">You are signed in as <span className="text-foreground font-semibold">{user?.role}</span>. You can manage accounts ranked below you.</p>

      {/* Create user */}
      {assignableRoles.length > 0 && (
        <form onSubmit={create} data-testid="create-user-form" className="mt-6 border border-border bg-card p-4 grid grid-cols-1 sm:grid-cols-5 gap-2 items-end">
          <input data-testid="cu-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="bg-background border border-border px-3 py-2 text-sm focus:outline-none focus:border-accent" />
          <input data-testid="cu-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="bg-background border border-border px-3 py-2 text-sm focus:outline-none focus:border-accent" />
          <input data-testid="cu-password" type="text" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password (6+)" className="bg-background border border-border px-3 py-2 text-sm focus:outline-none focus:border-accent" />
          <select data-testid="cu-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="bg-background border border-border px-3 py-2 text-sm focus:outline-none focus:border-accent uppercase font-mono text-xs">
            {assignableRoles.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <button data-testid="cu-submit" disabled={creating} className="flex items-center justify-center gap-1.5 bg-accent text-accent-foreground font-mono text-xs uppercase tracking-widest py-2.5 hover:bg-accent/90 disabled:opacity-60">
            {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />} Add
          </button>
        </form>
      )}

      {/* Search */}
      <div className="mt-6 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input data-testid="user-search" value={q} onChange={(e) => { setQ(e.target.value); load(e.target.value); }} placeholder="Search by name or email…" className="w-full bg-card border border-border pl-10 pr-4 py-2.5 font-mono text-sm focus:outline-none focus:border-accent" />
      </div>

      {/* Table */}
      <div className="mt-4 border border-border overflow-x-auto" data-testid="users-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-secondary/40 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              <th className="text-left p-3">User</th>
              <th className="text-left p-3">Role</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const manage = canManage(r);
              const self = r.email === user.email && !user.impersonated_by;
              return (
                <tr key={r.id} data-testid={`user-row-${r.email}`} className="border-b border-border last:border-0 hover:bg-secondary/30">
                  <td className="p-3">
                    <div className="font-serif font-semibold text-foreground">{r.name || "—"}{self && <span className="ml-2 font-mono text-[9px] uppercase text-accent">You</span>}</div>
                    <div className="font-mono text-[11px] text-muted-foreground">{r.email}</div>
                  </td>
                  <td className="p-3">
                    {manage ? (
                      <select data-testid={`role-${r.email}`} value={r.role} onChange={(e) => changeRole(r, e.target.value)} className="bg-background border border-border px-2 py-1 font-mono text-[10px] uppercase" style={{ color: ROLE_COLOR[r.role] }}>
                        {["user", "admin", "superadmin"].map((role) => <option key={role} value={role} disabled={!canAssign(role)}>{role}</option>)}
                      </select>
                    ) : (
                      <span className="font-mono text-[10px] uppercase px-2 py-0.5" style={{ color: ROLE_COLOR[r.role] }}>{r.role}</span>
                    )}
                  </td>
                  <td className="p-3">
                    {r.blocked ? <span className="font-mono text-[10px] uppercase text-destructive flex items-center gap-1"><Ban className="h-3 w-3" /> Blocked</span> : <span className="font-mono text-[10px] uppercase text-green-700 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Active</span>}
                  </td>
                  <td className="p-3">
                    {manage ? (
                      <div className="flex items-center justify-end gap-1.5">
                        <button data-testid={`emulate-${r.email}`} onClick={() => emulate(r)} title="Emulate" className="p-1.5 text-muted-foreground hover:text-accent"><Eye className="h-4 w-4" /></button>
                        <button data-testid={`pw-${r.email}`} onClick={() => resetPw(r)} title="Reset password" className="p-1.5 text-muted-foreground hover:text-foreground"><KeyRound className="h-4 w-4" /></button>
                        {r.blocked
                          ? <button data-testid={`unblock-${r.email}`} onClick={() => unblock(r)} title="Unblock" className="p-1.5 text-muted-foreground hover:text-green-700"><CheckCircle2 className="h-4 w-4" /></button>
                          : <button data-testid={`block-${r.email}`} onClick={() => block(r)} title="Block" className="p-1.5 text-muted-foreground hover:text-destructive"><Ban className="h-4 w-4" /></button>}
                        <button data-testid={`delete-${r.email}`} onClick={() => del(r)} title="Delete" className="p-1.5 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    ) : (
                      <div className="text-right font-mono text-[10px] uppercase text-muted-foreground/60 flex items-center justify-end gap-1"><ShieldAlert className="h-3 w-3" /> {self ? "Self" : "Locked"}</div>
                    )}
                  </td>
                </tr>
              );
            })}
            {!busy && rows.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">No users found.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
